import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  IconButton,
  Drawer,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

// API base - set `REACT_APP_API_BASE` in .env if needed
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

export default function NomineeAdd({ userid }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    id: null,
    name: '',
    gender: 'Male',
    mobile: '',
    aadhar: '',
    vidhansabha: 'Karad South',
    karad: { village: '', ganName: '', zpName: '', reference: '' },
    other: { village: '', taluka: '', district: '' },
    formDate: '',
    registrationNo: '',
    remarks: '',
  });
  const [editing, setEditing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    aadhar: '',
    village: '',
    registrationNo: '',
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/nominee-records`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      const data = await res.json();
      setRecords(data.map(mapFromApi));
    } catch (err) {
      console.error('Fetch error:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  function mapFromApi(r) {
    const isKarad = r.vidhansabha === 'Karad South';
    return {
      id: r.id,
      name: r.name || '',
      gender: r.gender || 'Male',
      mobile: r.mobile || '',
      aadhar: r.aadhar || '',
      vidhansabha: r.vidhansabha || 'Karad South',
      karad: {
        village: isKarad ? (r.village || '') : '',
        ganName: r.gan_name || '',
        zpName: r.zp_name || '',
        reference: r.reference || '',
      },
      other: {
        village: !isKarad ? (r.village || '') : '',
        taluka: r.taluka || '',
        district: r.district || '',
      },
      formDate: r.form_date || '',
      registrationNo: r.registration_no || '',
      remarks: r.remarks || '',
    };
  }

  function mapToApi(f, uid) {
    const isKarad = f.vidhansabha === 'Karad South';
    return {
      userid: uid,
      name: f.name,
      gender: f.gender,
      mobile: f.mobile,
      aadhar: f.aadhar,
      vidhansabha: f.vidhansabha,
      village: isKarad ? f.karad.village : f.other.village,
      gan_name: f.karad.ganName,
      zp_name: f.karad.zpName,
      reference: f.karad.reference,
      taluka: f.other.taluka,
      district: f.other.district,
      form_date: f.formDate || null,
      registration_no: f.registrationNo,
      remarks: f.remarks,
    };
  }

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name.startsWith('karad.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, karad: { ...s.karad, [k]: value } }));
      return;
    }
    if (name.startsWith('other.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, other: { ...s.other, [k]: value } }));
      return;
    }
    setForm((s) => ({ ...s, [name]: value }));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    const method = form.id ? 'PUT' : 'POST';
    const url = form.id ? `${API_BASE}/nominee-records/${form.id}` : `${API_BASE}/nominee-records`;

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(mapToApi(form, userid)),
      });
      if (res.ok) {
        fetchRecords();
        resetForm();
        setDrawerOpen(false);
      } else {
        const errData = await res.json();
        console.error('Save error Details:', errData);
        alert(`Failed to save record: ${errData.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Save error:', err);
    }
  }

  const handleEdit = useCallback((id) => {
    const rec = records.find((r) => r.id === id);
    if (rec) {
      setForm({ ...rec });
      setEditing(true);
      setDrawerOpen(true);
    }
  }, [records]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const res = await fetch(`${API_BASE}/nominee-records/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchRecords();
      } else {
        alert('Failed to delete');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  }, [fetchRecords]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }, []);

  const filteredRecords = useMemo(() => {
    const m = (val = '') => (val == null ? '' : String(val).toLowerCase());
    return records.filter((r) => {
      return (
        m(r.name).includes(m(filters.name)) &&
        m(r.mobile).includes(m(filters.mobile)) &&
        m(r.aadhar).includes(m(filters.aadhar)) &&
        (m(r.karad?.village).includes(m(filters.village)) || m(r.other?.village).includes(m(filters.village))) &&
        m(r.registrationNo).includes(m(filters.registrationNo))
      );
    });
  }, [records, filters]);

  function resetForm() {
    setForm({
      id: null,
      name: '',
      gender: 'Male',
      mobile: '',
      aadhar: '',
      vidhansabha: 'Karad South',
      karad: { village: '', ganName: '', zpName: '', reference: '' },
      other: { village: '', taluka: '', district: '' },
      formDate: '',
      registrationNo: '',
      remarks: '',
    });
    setEditing(false);
    setDrawerOpen(false);
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>Nominee Records</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add New Nominee
          </Button>
        </Box>

        {useMemo(() => (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Mobile', 'Aadhar', 'Village', 'Form Date', 'Registration No', 'Remarks', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="name" value={filters.name} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>, disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="mobile" value={filters.mobile} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="aadhar" value={filters.aadhar} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="village" value={filters.village} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="registrationNo" value={filters.registrationNo} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((r) => (
                  <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                    <TableCell>{r.mobile}</TableCell>
                    <TableCell>{r.aadhar}</TableCell>
                    <TableCell>{r.karad?.village || r.other?.village}</TableCell>
                    <TableCell>{r.formDate}</TableCell>
                    <TableCell>{r.registrationNo}</TableCell>
                    <TableCell>{r.remarks}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)} sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ), [filteredRecords, filters, handleFilterChange, handleEdit, handleDelete])}
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: { xs: '100vw', sm: 500 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
              {editing ? 'Edit Nominee' : 'Add New Nominee'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Mobile No" name="mobile" value={form.mobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Aadhar No" name="aadhar" value={form.aadhar} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="vidh-label">Vidhansabha</InputLabel>
              <Select labelId="vidh-label" label="Vidhansabha" name="vidhansabha" value={form.vidhansabha} onChange={handleChange}>
                <MenuItem value="Karad South">Karad South</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.vidhansabha === 'Karad South' ? (
              <Box sx={{ borderLeft: '3px solid #00acc1', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="karad.village" value={form.karad.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Gan Name" name="karad.ganName" value={form.karad.ganName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="ZP Name" name="karad.zpName" value={form.karad.zpName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Reference" name="karad.reference" value={form.karad.reference} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            ) : (
              <Box sx={{ borderLeft: '3px solid #8e24aa', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="other.village" value={form.other.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Talika" name="other.taluka" value={form.other.taluka} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="District" name="other.district" value={form.other.district} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            )}

            <TextField label="Form Date" name="formDate" type="date" InputLabelProps={{ shrink: true }} value={form.formDate} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Registration No" name="registrationNo" value={form.registrationNo} onChange={handleChange} fullWidth variant="outlined" />

            <TextField label="Remarks" name="remarks" value={form.remarks} onChange={handleChange} multiline rows={3} fullWidth variant="outlined" />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" sx={{ borderRadius: 2 }}>
              {editing ? 'Update' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={resetForm} fullWidth size="large" sx={{ borderRadius: 2 }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
